import { getCachedSubForSid } from './oidcManager';

const TS_BASE_PATH = '/ts/';

interface TrustShieldUrlParams {
    [key: string]: string | undefined;
}

/**
 * Build a TrustShield client URL with the given parameters.
 *
 * Second arg (always pass a `sid` when building iframe/tab URLs):
 *   - string               → SSO flow (adds `sso` + `SessionID`). Use when
 *                            opening a fresh tab where the user may need to
 *                            auto-login via the UV session.
 *   - { sid, sso: false }  → no SSO — for already-authenticated embedded iframes.
 *
 * If `sid` has already been resolved to a `sub` this tab (via
 * `initTrustShieldSSO`), appends `account=<sub>` so the TS SPA lands directly
 * in the right per-user partition.
 */
export function buildTrustShieldUrl(
    params: TrustShieldUrlParams,
    sidOrOptions?: string | { sid?: string; sso?: boolean }
): string {
    const opts: { sid?: string; sso?: boolean } = typeof sidOrOptions === 'string'
        ? { sid: sidOrOptions, sso: true }
        : (sidOrOptions ?? {});
    const { sid, sso } = opts;

    const searchParams = new URLSearchParams();
    if (sso) searchParams.set('sso', '');
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) searchParams.set(key, value);
    }
    if (sid) {
        if (sso) searchParams.set('SessionID', sid);
        const sub = getCachedSubForSid(sid);
        if (sub) searchParams.set('account', sub);
    }
    return `${TS_BASE_PATH}?${searchParams.toString()}`;
}

export const STATUS_BADGE_VARIANT: Record<string, string> = {
    DRAFT: 'secondary',
    PENDING: 'warning',
    CLEARED: 'success',
    RECONCILED: 'success',
    APPROVED: 'info',
    REJECTED: 'danger',
    CANCELLED: 'dark',
};

export function getStatusDisplayLabel(status: string): string {
    // Title-case the raw TrustShield status (CLEARED → Cleared, DRAFT → Draft)
    return status.charAt(0) + status.slice(1).toLowerCase();
}

const LDD_IFRAME_SELECTOR = '#lddIFrame > iframe';

/**
 * Notify the legacy ASP iframe that a TrustShield export completed,
 * so it can refresh export status indicators.
 */
export function notifyLegacyIframeExportComplete(): void {
    const iframe = document.querySelector(LDD_IFRAME_SELECTOR) as HTMLIFrameElement | null;
    iframe?.contentWindow?.postMessage(
        { type: 'trustShieldExportComplete' },
        window.location.origin
    );
}

interface MirrorArgs {
    hash: string;
    amountCents: number;
    description: string;
}

type MirrorFnName = 'mirrorReceiptToRealti' | 'mirrorPaymentToRealti';

function callRealtiMirror(fnName: MirrorFnName, args: MirrorArgs): boolean {
    const iframe = document.querySelector(LDD_IFRAME_SELECTOR) as HTMLIFrameElement | null;
    const tsExport = (iframe?.contentWindow as { TrustShieldExport?: Record<MirrorFnName, ((a: MirrorArgs) => unknown) | undefined> } | null)?.TrustShieldExport;
    const fn = tsExport?.[fnName];
    if (typeof fn !== 'function') {
        console.warn(`[TrustShield] ${fnName} not available on Realti iframe`);
        return false;
    }
    try {
        return fn(args) != null;
    } catch (err) {
        console.warn(`[TrustShield] ${fnName} threw:`, err);
        return false;
    }
}

/**
 * Mirror a freshly saved TrustShield deposit back into Realti so the receipt
 * also appears on the Realti file (Funds In + Statement of Adjustments).
 *
 * Reuse the original `items[0].hash` from the openNewDeposit payload — Realti
 * uses it to keep the row marked as exported. Returns false on failure
 * (function missing, validation null, or throw); the caller should surface a
 * non-fatal warning rather than unwinding the TrustShield save.
 */
export function mirrorReceiptToRealti(args: MirrorArgs): boolean {
    return callRealtiMirror('mirrorReceiptToRealti', args);
}

/**
 * Symmetric to mirrorReceiptToRealti for the new-payment flow. Reuse the
 * original `items[0].hash` from the openNewPayment payload so Realti keeps
 * the row linked to the TrustShield record via IssueAdjust/@TrustShieldHash.
 */
export function mirrorPaymentToRealti(args: MirrorArgs): boolean {
    return callRealtiMirror('mirrorPaymentToRealti', args);
}

export function formatCents(cents: number): string {
    return (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
}

/**
 * Format a date string for display. Handles both ISO ("2026-03-20") and
 * numeric DATEONLY ("20260320") formats from the TrustShield API.
 */
export function formatDate(dateStr: string): string {
    if (!dateStr) return '';

    let normalized = dateStr;
    // MSSQL DATEONLY can serialize as "20260320" — insert dashes
    if (/^\d{8}$/.test(dateStr)) {
        normalized = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
    }

    const date = new Date(normalized + 'T00:00:00');
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}
